<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { insightsApi } from '@/api/client'
import { Skeleton } from '@/components/ui/skeleton'
import InsightsCompetitorScatter from '@/components/InsightsCompetitorScatter.vue'
import InsightsCompetitorTable from '@/components/InsightsCompetitorTable.vue'
import InsightsInfoTip from '@/components/InsightsInfoTip.vue'
import {
    INSIGHTS_SIDES,
    currencySymbol,
    scopeQuery,
    type InsightsCompetitors,
    type InsightsScope,
} from '@/lib/insights'

/**
 * By competitor: a snapshot of the week, no history (ADR-027). The scatter places
 * every competitor against its own country's market; the tables give the detail.
 */
const props = defineProps<{
    scope: InsightsScope
    scopeKey: string
}>()

const { t } = useI18n()

const data = ref<InsightsCompetitors | null>(null)
const failed = ref(false)
let request = 0

const load = async () => {
    const token = ++request

    failed.value = false

    try {
        const response = await insightsApi.competitors(scopeQuery(props.scope))

        if (token === request) {
            data.value = response.data
        }
    } catch {
        if (token === request) {
            failed.value = true
        }
    }
}

watch(() => props.scopeKey, load, { immediate: true })

const sides = computed(() =>
    INSIGHTS_SIDES.flatMap((side) => {
        const rows = data.value?.[side] ?? null

        return rows === null ? [] : [{ side, rows }]
    }),
)
</script>

<template>
    <p v-if="failed" class="text-sm text-destructive">{{ t('common.errors.somethingWrong') }}</p>

    <div v-else-if="data === null" class="space-y-6">
        <Skeleton class="h-72 w-full" />
        <Skeleton class="h-64 w-full" />
    </div>

    <div v-else class="space-y-8">
        <div class="rounded-xl border bg-card p-6">
            <h2 class="mb-6 flex items-center gap-1.5 font-semibold">
                {{ t('insights.competitors.scatterTitle') }}
                <InsightsInfoTip :title="t('insights.competitors.scatterTitle')" :text="t('insights.tips.scatter')" />
            </h2>
            <InsightsCompetitorScatter :data="data" />
        </div>

        <InsightsCompetitorTable
            v-for="{ side, rows } in sides"
            :key="side"
            :side="side"
            :data="rows"
            :currency-symbol="currencySymbol(data.currency)"
        />

        <p class="rounded-lg bg-muted/50 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
            {{ t('insights.competitors.methodology') }}
        </p>
    </div>
</template>
